import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { gql, useQuery } from '@apollo/client';
import {
  FormControl,
  FormHelperText,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { accountSelectedSelector } from 'atoms';

export type SolicitationValue = {
  id: number;
  name: string;
};

const SOLICITATION_LIST = gql`
  query SolicitationList($isCloud: Boolean!, $webService: String, $masterCompanyId: Int!) {
    ServiceOrderDefectList(
      isCloud: $isCloud
      webService: $webService
      masterCompanyId: $masterCompanyId
    ) {
      id
      name
    }
  }
`;

function SolicitationSelect({
  selected,
  onChange,
  error,
  helperText,
  placeholder,
  name,
}: {
  selected: SolicitationValue | null;
  onChange: (value: SolicitationValue | null) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
  name: string;
}) {
  const accountSelected = useRecoilValue(accountSelectedSelector);
  const { data, loading } = useQuery<{
    ServiceOrderDefectList: SolicitationValue[];
  }>(SOLICITATION_LIST, {
    variables: {
      ...accountSelected,
    },
  });
  const { t } = useTranslation('service_order');
  const list = data?.ServiceOrderDefectList || [];

  return (
    <FormControl variant="outlined" fullWidth error={error}>
      <Autocomplete
        id="select-solicitation"
        loading={loading}
        autoSelect
        openOnFocus
        onChange={(e, value) => onChange(value)}
        value={selected}
        options={list}
        getOptionLabel={(option) => option.name || ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...{ ...params, name, placeholder }}
            sx={{ mt: 1 }}
            fullWidth
            label={t('solicitation')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

export default SolicitationSelect;
